  import React, { useState, useEffect, useMemo } from "react";
  import { Helmet } from "react-helmet";
  import { Link } from "react-router-dom";
  import Hero from "./components/Hero";
  import Seo from "./components/seo";
  

  /* ==============================
    THEME (match other pages)
  ============================== */

  const WRAP = "px-[1vw]";
  const MAXW = "max-w-10xl mx-auto";
  const FONT = "font-[Cormorant_Garamond]";
  const TEXT = "text-white/90";
  const PANEL = "relative overflow-hidden rounded-none border-2 border-white/30 shadow-[0_1px_2px_0_rgba(0,0,0,0.8)] backdrop-blur-lg bg-black/15";

  // icon glow tokens
const ICON_GLOW_BASE =
  "w-24 h-24 object-contain select-none pointer-events-none transition duration-200 ease-out drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]";

const ICON_GLOW_HOV =
  "group-hover:scale-105 motion-reduce:group-hover:scale-100 " +
  "group-hover:brightness-125 group-hover:contrast-110 " +
  "group-hover:drop-shadow-[0_0_2px_rgba(255,255,255,0.2)]"; // crisp white halo

  function formatFriday(dt) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(dt);
}
  /* ==============================
    SEO
  ============================== */
  const siteUrl = "https://tarotfate.com";
  const pageUrl = `${siteUrl}/`;
  const ogImage = `${siteUrl}/icons/og-cover.jpg`;
  const logoUrl = `${siteUrl}/icons/logo.webp`;

  const metaDescription =
    "Emotion-based online tarot readings. Choose your feeling, pick a spread, and receive a clear, marriage, study, business reflective message for love, career, and personal growth—no fortune telling, just grounded guidance.";
  const keywords = `
  online tarot, emotion-based tarot, free tarot reading, tarot card meanings, single card, three card, five card spread,
  upright and reversed meanings, love tarot, career tarot, personal growth, mindful tarot, reflective tarot, learn tarot, marriage tarot, study tarot, business tarot, broken heart tarot, inda love marriage, pakistani love marriage, business tarot
  `.replace(/\s+/g, " ").trim();

  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: logoUrl, width: 512, height: 512 }
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
        potentialAction: {
          "@type": "SearchAction",
          target: `${siteUrl}/tarot-card/{search_term}`,
          "query-input": "required name=search_term"
        }
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "TarotFate | Emotion-Based Online Tarot Readings",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@type": "ImageObject", url: ogImage, width: 1200, height: 630 },
        inLanguage: "en"
      }
    ]
  };

  // Image fallback helper (tries multiple sources)
function ImgFallback({ srcs, alt, className }) {
  const [i, setI] = React.useState(0);
  if (!srcs?.length) return null;
  return (
    <img
      src={srcs[i]}
      alt={alt}
      onError={() => setI((p) => (p + 1 < srcs.length ? p + 1 : p))}
      className={className}
      decoding="async"
      draggable="false"
    />
  );
}

// Quick Oracles just below Emotions
const QUICK_FEATURES = [
  {
    key: "yesno",
    title: "Yes / No Tarot",
    to: "/yes-no-tarot",
    // prefer your .web, then .webp, then .png
    srcs: ["/icons/yesno.web", "/icons/yesno.webp", "/icons/yesno.png"],
    cta: "▶ Start",
    explain: "Begin a focused one-card draw that whispers Yes / No, or Wait. Simple and direct.",
  },
  {
    key: "readmycard",
    title: "Read My Card(s)",
    to: "/readmycard",                    // <-- route for your Readymycard page
    srcs: ["/icons/readmycard.webp", "/icons/readmycard.png"],
    cta: "🔮 Open",
    explain: "Pick your own cards and get clear meanings for each, upright or reversed.",
  },
  {
    key: "tarotscope",
    title: "Tarotscope",
    to: "/tarotscope",
    srcs: ["/icons/tarotscope.web", "/icons/tartoscope.webp", "/icons/tarotscope.webp", "/icons/tarotscope.png"],
    cta: "✨ Explore",
    explain: "Discover how tarot blends with astrology to reveal guidance aligned with your zodiac sign.",
  },
];


  /* ==============================
    API + helpers
  ============================== */
  const API_BASE =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
    "https://tarotfate-api.tarotfate-com.workers.dev";

  const emotions = [
    { label: "Uncertain-Love", description: "Unsure if heart is truly seen and returned." },
    { label: "Heart-broken", description: "Mourning love that's been lost." },
    { label: "Anxious", description: "Worried about what's coming." },
    { label: "Confused", description: "Torn between choices or paths." },
    { label: "Lonely", description: "Craving love, connection, or understanding." },
    { label: "Hopeless", description: "Feeling like nothing will change." },
    { label: "Guilty", description: "Haunted by past decisions." },
    { label: "Betrayed", description: "Wounded by trust broken or promises lost." },
    { label: "Insecure", description: "Doubting your worth or place in someone's heart." },
    { label: "Overwhelmed", description: "Drowning in too many feelings or expectations." },
    { label: "Other", description: "Something else stirs your soul." }
  ];

  const spreads = [
    { value: 1, title: "The Whisper", icon: "Spread1.png", desc: "A single card to gently reflect your emotional state in this moment." },
    { value: 3, title: "The Thread", icon: "Spread3.png", desc: "A linear 3 cards spread that follows your journey: where you've been, where you are, where you're headed." },
    { value: 5, title: "The Mirror", icon: "Spread5.png", desc: "Five cards that dive deeper into the emotional terrain and guide your next move." }
  ];

  // word-count + excerpt helpers
  const wc = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;
  const excerptOf = (s, maxChars = 600) => (s || "").replace(/\s+/g, " ").slice(0, maxChars) + (s && s.length > maxChars ? "…" : "");

  // Friday anchor for weekly caching
  function fridayAnchorUTC(date = new Date(), hourUTC = 9) {
    const now = date;
    const dow = now.getUTCDay(); // 0 Sun … 5 Fri
    const daysSinceFriday = (7 + dow - 5) % 7;
    const lastFriday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    lastFriday.setUTCDate(lastFriday.getUTCDate() - daysSinceFriday);
    lastFriday.setUTCHours(hourUTC, 0, 0, 0);
    if (now < lastFriday) lastFriday.setUTCDate(lastFriday.getUTCDate() - 7);
    return lastFriday;
  }

  /* ==============================
    Component
  ============================== */
  export default function TarotApp() {
    // existing state
    const [selectedEmotion, setSelectedEmotion] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [customWhisper, setCustomWhisper] = useState("");
    const [readingStarted, setReadingStarted] = useState(false);
    const [selectedSpread, setSelectedSpread] = useState(null);
    const [lastReadingTime, setLastReadingTime] = useState(null);
    const [cooldownMessage, setCooldownMessage] = useState("");
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [mockReading, setMockReading] = useState("");
    const [loading, setLoading] = useState(false);
    const [showDonation, setShowDonation] = useState(false);
    const [customAmount, setCustomAmount] = useState("");
    const [showCopiedToast, setShowCopiedToast] = useState(false);
    const [popupStep, setPopupStep] = useState(1);

    // NEW: homepage teasers state
    
    const [ffTitle, setFfTitle] = useState("Fate Friday");
    const [ffNote, setFfNote] = useState("");
    const [ffLoading, setFfLoading] = useState(true);

    const [fmHero, setFmHero] = useState(null); // { name, dateUTC, meaning }
    const [fmNote, setFmNote] = useState("");
    const [fmLoading, setFmLoading] = useState(true);

    const donationAmounts = [5, 10, 20, 50, 100];

    // Step progression
    useEffect(() => {
      if (showPopup && !readingStarted) setPopupStep(selectedSpread ? 2 : 1);
    }, [selectedSpread, showPopup, readingStarted]);

    // Cooldown countdown
    useEffect(() => {
      let id;
      if (cooldownSeconds > 0) {
        id = setInterval(() => setCooldownSeconds((p) => p - 1), 1000);
      } else if (cooldownSeconds === 0 && cooldownMessage) {
        setCooldownMessage("");
      }
      return () => clearInterval(id);
    }, [cooldownSeconds, cooldownMessage]);

    // Scroll lock
    const lockScroll = () => {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    };
    const unlockScroll = () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };

    const openPopup = () => {
      setShowPopup(true);
      setPopupStep(1);
      setSelectedSpread(null);
      lockScroll();
    };

    const reset = () => {
      unlockScroll();
      setSelectedEmotion(null);
      setShowPopup(false);
      setCustomWhisper("");
      setReadingStarted(false);
      setSelectedSpread(null);
      setCooldownMessage("");
      setCooldownSeconds(0);
      setMockReading("");
      setLoading(false);
      setPopupStep(1);
    };

    const goBackToStep1 = () => {
      setPopupStep(1);
      setSelectedSpread(null);
      setReadingStarted(false);
    };

    const handleBackButton = () => {
      if (popupStep === 3) goBackToStep1();
      else if (popupStep === 2) {
        setPopupStep(1);
        setSelectedSpread(null);
      }
    };

    function formatTextWithMarkdown(text) {
      const bold = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      return bold.replace(/\*(.+?)\*/g, "<em>$1</em>");
    }

    const isOtherSelected = (selectedEmotion || "").toLowerCase() === "other";
    const canProceed = !!selectedSpread && (!isOtherSelected || customWhisper.trim().length > 0);

    // ==============================
    // Tarot reading popup actions
    // ==============================
    const handleReadTarot = async () => {
      if (!selectedSpread) {
        setCooldownMessage("Select a card spread first");
        setTimeout(() => setCooldownMessage(""), 2000);
        return;
      }
      if (isOtherSelected && !customWhisper.trim()) {
        setCooldownMessage("Please describe what you're feeling to proceed.");
        setTimeout(() => setCooldownMessage(""), 2000);
        return;
      }

      const now = Date.now();
      if (lastReadingTime && now - lastReadingTime < 60000) {
        const secondsLeft = Math.ceil((60000 - (now - lastReadingTime)) / 1000);
        setCooldownSeconds(secondsLeft);
        setCooldownMessage(
          `(Please wait ${secondsLeft} seconds) The tarot speaks in whispers, not in haste. Let its magic settle before seeking again.`
        );
        return;
      }

      setLastReadingTime(now);
      setReadingStarted(true);
      setPopupStep(3);
      setCooldownMessage("");
      setCooldownSeconds(0);
      setLoading(true);

      try {
        const res = await fetch(`${API_BASE}/api/tarot`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            emotion: selectedEmotion,
            spread: selectedSpread,
            whisper: customWhisper
          })
        });
        const data = await res.json();
        if (data.reading) setMockReading(data.reading);
        else setMockReading("The mystic veil could not be lifted to feel your emotion at this moment. Please try again shortly.");
      } catch (err) {
        setMockReading("The mystic veil could not be lifted to feel your emotion at this moment. Please try again shortly.");
      } finally {
        setLoading(false);
      }
    };

    // ==============================
    // NEW: Homepage teasers (Fate Friday + Full Moon)
    // ==============================
    useEffect(() => {
      let cancel = false;

      // FATE FRIDAY
      (async () => {
        try {
          setFfLoading(true);
          const anchor = fridayAnchorUTC();
          const ffKey = `tf_ff_long_home_${anchor.toISOString().slice(0, 10)}`;
          const cached = localStorage.getItem(ffKey);
          if (cached) {
            const p = JSON.parse(cached);
            if (!cancel) {
              setFfTitle(p.title || "Fate Friday");
              setFfNote(p.note || "");
              setFfLoading(false);
            }
            return;
          }
          const r = await fetch(`${API_BASE}/api/fatefriday`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              zodiac: "",
              theme: "reflection",
              tone: "gentle",
              cardName: "",
              weekAnchorUTC: anchor.toISOString()
            })
          });
          const d = await r.json();
          if (!r.ok) throw new Error(d?.message || `HTTP ${r.status}`);
          const payload = { title: d?.title || "Fate Friday", note: d?.note || "" };
          if (!cancel) {
            setFfTitle(payload.title);
            setFfNote(payload.note);
          }
          try { localStorage.setItem(ffKey, JSON.stringify(payload)); } catch {}
        } catch {
          if (!cancel) {
            setFfTitle("Fate Friday");
            setFfNote("This week’s guide focuses on choosing one action that steadies your day and clears a small path forward.");
          }
        } finally {
          if (!cancel) setFfLoading(false);
        }
      })();

      // FULL MOON
      (async () => {
        try {
          setFmLoading(true);
          const y = new Date().getUTCFullYear();
          const r = await fetch(`${API_BASE}/api/fullmoons?year=${y}`);
          const d = await r.json();
          if (!r.ok) throw new Error(d?.message || `HTTP ${r.status}`);
          const next = d?.next || d?.previous;
          if (!next) throw new Error("No moon found");
          if (!cancel) setFmHero(next);

          const fmKey = `tf_fm_long_home_${next.dateUTC}`;
          const cached = localStorage.getItem(fmKey);
          if (cached) {
            const p = JSON.parse(cached);
            if (!cancel) {
              setFmNote(p.note || "");
              setFmLoading(false);
            }
            return;
          }
          const r2 = await fetch(`${API_BASE}/api/moon-blessing`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              zodiac: "",
              moonName: next.name || "Full Moon",
              fullMoonDateUTC: next.dateUTC,
              meaning: next.meaning || "",
              tone: "gentle"
            })
          });
          const d2 = await r2.json();
          if (!r2.ok) throw new Error(d2?.message || `HTTP ${r2.status}`);
          const payload = { note: d2?.note || "" };
          if (!cancel) setFmNote(payload.note);
          try { localStorage.setItem(fmKey, JSON.stringify(payload)); } catch {}
        } catch {
          if (!cancel) setFmNote("A practical moon guide will be available soon.");
        } finally {
          if (!cancel) setFmLoading(false);
        }
      })();

      return () => { cancel = true; };
    }, []);

    // ==============================
    // UI helpers
    // ==============================
    const ffPublished = useMemo(() => fridayAnchorUTC(), []);
    const getEmotionImagePath = (label) => `/icons/${label.replace(/\s+/g, "_")}.webp`;
    const fmLocal = useMemo(() => {
      if (!fmHero?.dateUTC) return "";
      try {
        return new Intl.DateTimeFormat(undefined, {
          year: "numeric", month: "long", day: "numeric",
          hour: "numeric", minute: "2-digit", timeZoneName: "short",
        }).format(new Date(fmHero.dateUTC));
      } catch {
        return fmHero.dateUTC;
      }
    }, [fmHero]);

    return (
      <div className={`${FONT} text-white min-h-screen`}>
        {/* SEO */}
        <Seo
          title="TarotFate | Emotion-Based Online Tarot Readings"
          description={metaDescription}
          url={pageUrl}
          image={ogImage}
          schema={schemaGraph}
          keywords={keywords}
        />

        {/* Prefetch feature pages + preload icons */}
        <Helmet>
          <link rel="prefetch" href="/fate-friday" />
          <link rel="prefetch" href="/full-moon" />
          <link rel="prefetch" href="/read-my-card" />
          <link rel="preload" as="image" href="/icons/readmycard.webp" />
          {emotions.map((e) => (
            <link key={e.label} rel="preload" as="image" href={`/icons/${e.label.replace(/\s+/g, "_")}.webp`} />
          ))}
          {spreads.map((s) => (
            <link key={s.icon} rel="preload" as="image" href={`/icons/${s.icon}`} />
          ))}
        </Helmet>

        {/* HERO */}
        <header className={`text-center ${WRAP} ${MAXW} pt-8 pb-2`}>
          <Hero />
          <div aria-hidden className="h-6 sm:h-8 md:h-8 lg:h-8 xl:h-8" />
          <p className="text-white text-lg drop-shadow-[0_0_1px_rgba(0,0,0,0.9)]">
            Let the mystic tarot cards echo the truth already resting in your soul.
          </p>
          <br />
          <h3 className="text-lg font-semibold text-white">What are you feeling right now?</h3>
          <br />
        </header>

        {/* Emotion grid */}
        <section className={`${WRAP} ${MAXW}`}>
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10 px-4 max-w-[1200px] mx-auto select-none">
           {emotions.map((e) => (
<button
  type="button"
  key={e.label}
  onClick={() => {
    setSelectedEmotion(e.label);
    openPopup();
    if (e.label.toLowerCase() === "other") setCustomWhisper("");
  }}
  className="group w-[45%] sm:w-[30%] md:w-[170px] rounded-none cursor-pointer transition-all duration-100 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(88,64,120,0.4)] focus:outline-none focus:ring-2 focus:ring-[#a991d4]/70 text-[#3f2d61] backdrop-blur-md bg-gradient-to-b from-white/35 via-[#14656a]/35 to-[#0f3d3f]/12 border-[1px] border-transparent [background-clip:padding-box,border-box] [border-image:linear-gradient(135deg,#a991d4,#cbb7ea,#cbb7ea,#2b9cb3,#2b9cb3)_1] select-none shadow-[0_2px_6px_0_rgba(0,0,0,0.8)]"
>
  {/* ICON + GLOW */}
  <div className="flex justify-center pt-4">
    <div className="relative">
      <img
        src={getEmotionImagePath(e.label)}
        alt={`${e.label} emotion icon`}
        width={96}
        height={96}
        fetchPriority="high"
        decoding="async"
        draggable="false"
        className={`${ICON_GLOW_BASE} ${ICON_GLOW_HOV}`}
      />
      {/* white bloom on hover */}
      <span
        aria-hidden
        className="
          absolute inset-0 rounded-lg opacity-0
          group-hover:opacity-100 transition duration-200 ease-out
          blur-[16px]
          bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0.55),transparent_70%)]
          pointer-events-none
        "
      />
    </div>
  </div>

  {/* LABELS */}
  <div className="flex flex-col items-center text-center px-3 pb-4 mt-2 select-none">
    <h4 className="text-base font-semibold text-white">
      {e.label.replace("_", " ")}
    </h4>

    {/* ✨ mystic divider */}
    <div className="mt-1 w-11/12 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>

    <p className="pt-3 text-sm italic text-white/85">{e.description}</p>
  </div>
</button>

))}

          </div>
        </section>

{/* Quick Oracles: Yes/No + Tarotscope (Poster-style, sharp edges) */}
<section className={`${WRAP} ${MAXW} mt-8`}>
  <div className="max-w-[1200px] mx-auto px-4">
    <div className="text-center mb-8">
      
      <div role="separator" className="my-2 h-px w-full bg-gradient-to-r from-transparent via-white/70 to-transparent" />
    </div>

    <div className="grid gap-6 sm:grid-cols-3">
      {QUICK_FEATURES.map((f) => (
        <Link
          key={f.key}
          to={f.to}
          className="
            group relative overflow-hidden select-none
            shadow-[0_1px_2px_rgba(0,0,0,0.35)] backdrop-blur-lg
            bg-gradient-to-b from-[#0B5991]/10 via-[#167596]/10 to-[#0f3d3f]/40
            hover:shadow-[0_1px_4px_rgba(0,0,0,0.45)]
            transition
          "
        >
          {/* thin poster outline */}
          <div className="absolute inset-0 border border-white/25 pointer-events-none" />

          {/* top-down white gradient overlay */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none
                       bg-gradient-to-b from-white/20 via-transparent to-transparent"
          />

          {/* content */}
          <div className="relative z-[1] flex flex-col items-center text-center px-6 py-7 min-h-[320px]">
            {/* icon on top (smaller on mobile) */}
            <ImgFallback
              srcs={f.srcs}
              alt={`${f.title} logo`}
              className="
                w-full h-auto object-contain max-h-[160px] sm:max-h-[180px] md:max-h-[200px]
                drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]
                transition group-hover:brightness-110
              "
            />

            {/* divider line */}
      <div role="separator" className="mt-6 h-px w-full bg-gradient-to-r from-transparent via-white/70 to-transparent" />

            {/* Explanation (italic, readable like your sample) */}
            <p className="mt-3 text-white/85  text-base leading-7">
              {f.explain}
            </p>
          </div>

          {/* gentle hover rim glow */}
          <div className="absolute inset-0 pointer-events-none ring-0 group-hover:ring-1 group-hover:ring-white/80 transition" />
        </Link>
      ))}
    </div>
  </div>
</section>

      

        

        {/* Teasers row: Fate Friday + Full Moon */}
        <main className={`${WRAP} ${MAXW} mt-8 pb-12`}>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Fate Friday teaser */}
            <section className={`${PANEL} p-4 sm:p-6 text-left`}>
              <div className="relative z-[1]">
                <div className="flex items-baseline justify-between gap-3">
  <p className="uppercase tracking-[0.25em] text-sm text-white/85">Fate Friday</p>
  
</div>
<div
  role="separator"
  className="my-2 h-px w-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
/>
<h2 className="text-lg sm:text-xl font-semibold text-white/95 mt-1">
  {ffLoading ? "Fate Friday" : ffTitle || "Fate Friday"}
</h2>
<span className="text-sm sm:text-sm text-white/75">
    Published Weekly: {formatFriday(ffPublished)}
  </span>

                <p className={`${TEXT} text-base leading-7 mt-3`}>
                  {ffLoading ? "Loading…" : excerptOf(ffNote)}
                </p>
                <div className="mt-4">
                  <Link
                    to="/fate-friday"
                    className="inline-block px-3 py-1.5 rounded-full bg-white/10 border border-white/30 text-white/95 text-sm hover:bg-white/20 hover:border-white/40 transition"
                  >
                    Read Fate Friday guide
                  </Link>
                </div>
              </div>
              <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            </section>

            {/* Full Moon teaser */}
            <section className={`${PANEL} p-4 sm:p-6 text-left`}>
              <div className="relative z-[1]">
                <p className="uppercase tracking-[0.25em] text-sm text-white/85">Full Moon Highlight</p>
                <div
  role="separator"
  className="my-2 h-px w-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
/>

                <h2 className="text-lg sm:text-xl font-semibold text-white/95 mt-1">
                  {fmHero?.name || "Full Moon"}
                </h2>
                <p className="text-sm text-white/85 mt-0.5">
                  {fmHero ? fmLocal : "—"}
                </p>
                <p className={`${TEXT} text-base leading-7 mt-3`}>
                  {fmLoading ? "Loading…" : excerptOf(fmNote)}
                </p>
                <div className="mt-4">
                  <Link
                    to="/full-moon"
                    className="inline-block px-3 py-1.5 rounded-full bg-white/10 border border-white/30 text-white/95 text-sm hover:bg-white/20 hover:border-white/40 transition"
                  >
                    Read the Full Moon guide
                  </Link>
                </div>
              </div>
              <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            </section>
          </div>
        </main>

        {/* POPUP */}
        {showPopup && (
          <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" aria-hidden="true" />
            <div
              role="dialog"
              aria-modal="true"
              className="relative z-10 w-[98%] sm:w-[95%] md:w-[500px] lg:w-[600px] max-h-[80vh] sm:max-h-[82vh] md:max-h-[85vh]
              overflow-y-auto overscroll-contain backdrop-blur-xl 
              bg-gradient-to-b from-[#0B8FBF]/55 via-[#0B67AC]/30 to-[#0B67AE]/10 rounded-xl p-5 shadow-[0_10px_30px_rgba(0,0,0,0.25)] border border-white/60 text-[#072021] animate-fadeIn"
            >
              {/* Back button (steps 2–3) */}
              {popupStep !== 1 && !loading && (
                <button
                  onClick={handleBackButton}
                  className="absolute top-4 left-4 bg-white/20 text-[#f3eaff]/80 
                    w-7 h-7 flex items-center justify-center 
                    rounded-full text-xl font-normal 
                    leading-none hover:text-[#0f3d3f]/70 hover:bg-white/60 transition"
                  aria-label="Back"
                >
                  ←
                </button>
              )}
              {/* Close */}
              <button
                onClick={reset}
                className="absolute top-4 right-4 bg-white/20 text-[#f3eaff]/80 
                  w-7 h-7 flex items-center justify-center 
                  rounded-full text-2xl font-normal 
                  leading-none hover:text-[#0f3d3f]/70 hover:bg-white/60 transition"
                aria-label="Close"
              >
                ×
              </button>

              {/* Progress dots */}
              <div className="flex justify-center mb-2 mt-2">
                <div className="flex space-x-2">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className={`w-3 h-3 rounded-full ${popupStep >= step ? "bg-[#2b9cb3]" : "bg-white/30"}`} />
                  ))}
                </div>
              </div>

              {!readingStarted ? (
                <>
                  {/* Heading */}
                  {popupStep === 1 ? (
                    <h3 className="text-xl font-bold text-[#f3eaff] mb-1">{selectedEmotion}</h3>
                  ) : (
                    <h3 className="text-xl font-bold text-[#f3eaff] mb-1">
                      {spreads.find((s) => s.value === selectedSpread)?.title}
                    </h3>
                  )}

                  {/* Divider */}
                  <div className="flex items-center justify-center mb-1">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
                    <span className="px-4 text-[#f3eaff]/70 text-xl drop-shadow-[0_0_6px_rgba(203,183,234,0.8)]">✦</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
                  </div>

                  {/* Step 1: Spread selection */}
                  {popupStep === 1 && (
                    <>
                      <p className="text-base font-normal mb-3 text-[#f3eaff]/80">
                        Select a 'card spread' below. Each tarot card will be drawn in tune with your emotion, offering insight that is uniquely yours.
                      </p>

                      <div className="flex flex-col gap-3 mb-2">
                        {spreads.map((s) => {
                          const selected = selectedSpread === s.value;
                          return (
                            <button
                              type="button"
                              key={s.value}
                              onClick={() => {
                                setSelectedSpread(s.value);
                                if (!isOtherSelected) setTimeout(() => setPopupStep(2), 300);
                              }}
                              className={`
                                rounded-md cursor-pointer p-3 text-left
                                transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(88,64,120,0.4)]
                                focus:outline-none focus:ring-2 focus:ring-[#a991d4]/70 text-[#3f2d61] backdrop-blur-md
                                bg-gradient-to-b from-[#0f3d3f]/5 via-[#14656a]/15 to-[#0f3d3f]/12 border-[1px] border-transparent
                                [background-clip:padding-box,border-box]
                                [border-image:linear-gradient(135deg,#a991d4,#cbb7ea,#cbb7ea,#2b9cb3,#2b9cb3)_1]
                                select-none shadow-[0_4px_20px_0_rgba(0,0,0,0.8)]
                                ${selected ? "ring-2 ring-white scale-[1.02] bg-gradient-to-b from-[#2b9cb3]/20 via-[#2b9cb3]/30 to-[#2b9cb3]/20" : ""}
                              `}
                            >
                              <div className="flex items-center gap-4">
                                <div className="text-base font-medium text-[#f3eaff]">{s.title}</div>
                                <img
                                  src={`/icons/${s.icon}`}
                                  alt={`${s.value} card spread icon`}
                                  className="h-6 object-contain drop-shadow-[0_1px_1px_rgba(169,145,212,0.3)]"
                                  draggable="false"
                                />
                              </div>
                              <p className="mt-2 text-base italic text-white/80">{s.desc}</p>
                            </button>
                          );
                        })}
                      </div>

                      {/* Cooldown */}
                      {cooldownMessage && (
                        <div className="mt-4 p-3 bg-[#0f3d3f]/40 rounded-lg border border-[#2b9cb3]/50">
                          <p className="text-sm text-[#f3eaff]">
                            {cooldownMessage}
                            {cooldownSeconds > 0 && (
                              <span className="block mt-1 text-[#cbb7ea] font-medium">Please wait {cooldownSeconds} seconds</span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Ads placeholder */}
                    
                    </>
                  )}

                  {/* Step 2: Whisper */}
                  {popupStep === 2 && (
                    <>
                      <p className="text-base font-normal mb-2 text-[#f3eaff] text-center">
                        {isOtherSelected
                          ? "To proceed with 'Other', please describe what you're feeling."
                          : "✦ For a deeper & more personalized tarot reading, also share your heart's whispers with the universe."}
                      </p>

                      <textarea
                        value={customWhisper}
                        onChange={(e) => setCustomWhisper(e.target.value)}
                        className="w-full border border-[#0f3d3f] bg-white rounded p-3 text-base text-gray-1000 placeholder-[#6f7373] mb-3"
                        rows={3}
                        placeholder={isOtherSelected ? "Write your emotion (required)" : "Write your whispers here (optional)"}
                      />

                      <p className="text-sm text-[#fff] italic mb-4 text-center">Center your spirit and let your truth rise</p>

                      {cooldownMessage && (
                        <div className="mb-4 p-3 bg-[#0f3d3f]/40 rounded-lg border border-[#2b9cb3]/50">
                          <p className="text-sm text-[#f3eaff]">
                            {cooldownMessage}
                            {cooldownSeconds > 0 && (
                              <span className="block mt-1 text-[#cbb7ea] font-medium">Please wait {cooldownSeconds} seconds</span>
                            )}
                          </p>
                        </div>
                      )}

                      <button
                        onClick={handleReadTarot}
                        className={`w-full px-6 py-3 rounded-xl text-lg font-bold transition-all duration-100 border-2
                          ${
                            canProceed && cooldownSeconds === 0
                              ? "bg-gradient-to-r from-[#2b9cb3] to-[#2b9cb3] border-white/50 text-white shadow-xl hover:shadow-lg hover:from-[#14656a] hover:via-[#2b9cb3] hover:to-[#14656a]"
                              : "bg-gray-200 text-gray-400 border-[#2b9cb3] cursor-not-allowed"
                          }`}
                        disabled={!canProceed || cooldownSeconds > 0}
                      >
                        {cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : "Read My Tarot"}
                      </button>

                      {/* Ads placeholder */}
                     
                    </>
                  )}
                </>
              ) : loading ? (
                <div className="flex flex-col items-center justify-center py-10 animate-pulse">
                  <div className="text-lg font-bold text-[#f3eaff] text-center max-w-sm">Wait a moment please...</div>
                  <div className="text-lg text-[#f3eaff] italic text-center max-w-sm">
                    <br />"In the hush before insight, the cards are being born from stardust and silence..."
                  </div>
                </div>
              ) : (
                <>
                  <h4 className="text-lg font-semibold text-[#f3eaff] mb-3">Your Mystic Tarot Card Reading</h4>
                  <div className="bg-white/90 bg-opacity-95 border border-[#cbb7ea] rounded-lg shadow-md p-6 text-left text-[#0A2829] text-[17px] leading-relaxed space-y-1">
                    {mockReading.split("\n\n").map((para, index, arr) => (
                      <div key={index}>
                        <p
                          dangerouslySetInnerHTML={{ __html: formatTextWithMarkdown(para) }}
                          className="whitespace-pre-wrap"
                        />
                        {index < arr.length - 1 && <div className="flex justify-center my-1 text-[#cbb7ea] text-xl"></div>}
                      </div>
                    ))}
                  </div>

                  {/* Share + Copy */}
                  <div className="flex justify-between items-center mt-4 gap-2">
                    <button
                      onClick={async () => {
                        if (navigator.share) {
                          await navigator.share({ title: "My Tarot Reading ✨", text: mockReading });
                        } else {
                          alert("Sharing is not supported on your device.");
                        }
                      }}
                      className="text-sm text-white bg-[#14656a] hover:bg-[#2b9cb3]/70 border border-white hover:border-white px-4 py-1.5 rounded-full transition"
                    >
                      🔗 Share
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(mockReading);
                        setShowCopiedToast(true);
                        setTimeout(() => setShowCopiedToast(false), 2000);
                      }}
                      className="text-sm text-white bg-[#14656a] hover:bg-[#2b9cb3]/70 border border-white hover:border-white px-4 py-1.5 rounded-full transition"
                    >
                      🧾 Copy
                    </button>
                  </div>
                  <div className="flex flex-col items-end mt-1">
                    {showCopiedToast && <div className="mt-1 text-xs text-white/80 bg-transparent px-2 py-1">Copied ✓</div>}
                  </div>

                  {/* Donation */}
                  <div className="mt-6 bg-white/90 p-4 rounded-lg border border-[#decbaa]">
                    <p className="text-sm font-normal text-[#0f3d3f]">
                      If this space has brought you clarity, comfort, or even a moment of peace, consider leaving an offering to help keep the light on for others who may wander in.
                    </p>
                    <div className="mt-4 flex flex-col items-center">
                      <button
                        onClick={() => setShowDonation(!showDonation)}
                        className="bg-[#14656a] text-white px-5 py-2 rounded-full shadow hover:bg-[#2b9cb3] hover:border-[#14656a] transition"
                      >
                        💖 Donate via PayPal
                      </button>
                      {showDonation && (
                        <div className="mt-4 w-full flex flex-col items-center space-y-3">
                          <div className="flex flex-wrap justify-center gap-3">
                            {donationAmounts.map((amount) => (
                              <a
                                key={amount}
                                href={`https://www.paypal.me/tarotfatecom/${amount}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white border border-[#14656a] text-[#14656a] px-4 py-2 rounded-xl text-sm hover:bg-[#14656a] hover:text-white transition"
                              >
                                ${amount}
                              </a>
                            ))}
                          </div>
                          <div className="text-center mt-3">
                            <p className="text-sm text-[#14656a] mb-1">Or enter your own amount (max $1,000)</p>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={customAmount}
                              onChange={(e) => {
                                const value = e.target.value.trim();
                                const isValid = /^(\d+(\.\d{0,2})?)?$/.test(value);
                                if (isValid && parseFloat(value || "0") <= 1000) setCustomAmount(value);
                              }}
                              className="border border-[#14656a] rounded px-3 py-1 w-32 text-center text-[#14656a]"
                              placeholder="Other ($)"
                            />
                            {customAmount && parseFloat(customAmount) > 0 && (
                              <div className="mt-2">
                                <a
                                  href={`https://www.paypal.me/tarotfatecom/${customAmount}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block bg-[#14656a] text-white px-4 py-1.5 rounded-full shadow-lg hover:bg-[#2b9cb3] transition text-sm"
                                >
                                  Donate ${customAmount}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
